import React, { ReactNode, useState } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  useTheme,
  alpha,
  useMediaQuery,
} from "@mui/material";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartData,
  ChartOptions,
} from "chart.js";
import { GoogleMap, Marker, useLoadScript } from "@react-google-maps/api";
import {
  useGetAllFeedbacks,
  useGetAllPropertiesList,
  useGetAllUsers,
} from "../../hooks/query/useSuperAdmin";
import FallbackLoader from "../../components/loading/FallBackLoader";
import GroupIcon from "@mui/icons-material/Group";
import ForumIcon from "@mui/icons-material/Forum";
import MapsHomeWorkIcon from "@mui/icons-material/MapsHomeWork";

// Type Definitions
interface User {
  id: number;
  name: string;
  email: string;
  phoneNumber: string;
  // dateCreated: string;
}

interface Feedback {
  id: number;
  userName: string;
  comment: string;
  rating: number;
  userPhoneNumber: string;
}

interface CountCardItem {
  title: string;
  count: number;
  icon: ReactNode;
}

interface TableSection {
  title: string;
  data: User[] | Feedback[];
  page: number;
  rowsPerPage: number;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  columns: Array<{ key: string; label: string }>;
}

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const DashboardPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const primaryColor = "#bb0000";

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: "AIzaSyCBMjLiUIKGjqXDuEq0LnSJNzMgvGmyBSQ",
  });

  const {
    data: users,
    isLoading: isUserLoading,
    error: isUserError,
  } = useGetAllUsers();
  const {
    data: feedbacks,
    isLoading: isFeedbackLoading,
    // error: isFeedbackError,
  } = useGetAllFeedbacks();
  const {
    data: property = [],
    isLoading: isPropertyLoading,
    // error: isPropertyError,
  } = useGetAllPropertiesList();

  console.log(users);
  console.log(isUserError);
  console.log(isUserLoading);

  const monthlyUserCounts = Array(12).fill(0);
  users?.forEach((user: any) => {
    const month = new Date(user.createdAt).getMonth();
    monthlyUserCounts[month] += 1;
  });

  const isValidCoordinate = (value: any) =>
    typeof value === "number" && !isNaN(value);

  const validProperties =
    property?.filter(
      (p: any) =>
        isValidCoordinate(parseFloat(p.latitude)) &&
        isValidCoordinate(parseFloat(p.longitude))
    ) || [];

  const defaultCenter = { lat: 27.7172, lng: 85.324 };

  const mapCenter =
    validProperties.length > 0
      ? {
          lat: parseFloat(validProperties[0].latitude),
          lng: parseFloat(validProperties[0].longitude),
        }
      : defaultCenter;

  const userCount: number = users?.length || 0;
  const feedbackCount: number = feedbacks?.length || 0;
  const propertyCount: number = property?.length || 0;

  const userChartData: ChartData<"line"> = {
    labels: [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ],
    datasets: [
      {
        label: "Users Created",
        data: monthlyUserCounts,
        borderColor: primaryColor,
        backgroundColor: alpha(primaryColor, 0.2),
        tension: 0.4,
        borderWidth: 3,
      },
    ],
  };

  const chartOptions: ChartOptions<"line"> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
    },

    scales: {
      x: {
        grid: {
          color: alpha(primaryColor, 0.1),
        },
        ticks: {
          color: theme.palette.text.secondary,
          autoSkip: isMobile,
          maxTicksLimit: isMobile ? 5 : 12,
        },
      },
      y: {
        grid: {
          color: alpha(primaryColor, 0.1),
        },
        ticks: {
          color: theme.palette.text.secondary,
        },
      },
    },
  };

  const mapContainerStyle = {
    width: "100%",
    height: isMobile ? "250px" : "400px",
  };

  const [userPage, setUserPage] = useState<number>(0);
  const [userRowsPerPage, setUserRowsPerPage] = useState<number>(5);
  const [feedbackPage, setFeedbackPage] = useState<number>(0);
  const [feedbackRowsPerPage, setFeedbackRowsPerPage] = useState<number>(5);

  const handleUserPageChange = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    console.log(event);
    setUserPage(newPage);
  };

  const handleUserRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => setUserRowsPerPage(parseInt(event.target.value, 10));

  const handleFeedbackPageChange = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    console.log(event);
    setFeedbackPage(newPage);
  };

  const handleFeedbackRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => setFeedbackRowsPerPage(parseInt(event.target.value, 10));

  const countCardItems: CountCardItem[] = [
    { title: "Users", count: userCount, icon: <GroupIcon fontSize="large" /> },
    {
      title: "Feedbacks",
      count: feedbackCount,
      icon: <ForumIcon fontSize="large" />,
    },
    {
      title: "Properties",
      count: propertyCount,
      icon: <MapsHomeWorkIcon fontSize="large" />,
    },
  ];

  const tableSections: TableSection[] = [
    {
      title: "Users List",
      data: users || [],
      page: userPage,
      rowsPerPage: userRowsPerPage,
      onPageChange: handleUserPageChange,
      onRowsPerPageChange: handleUserRowsPerPageChange,
      columns: [
        { key: "sn", label: "SN" },
        { key: "name", label: "Name" },
        { key: "email", label: "Email" },
        { key: "phoneNumber", label: "Phone No." },
        // { key: "dateCreated", label: "Date Created" },
      ],
    },
    {
      title: "Feedbacks List",
      data: feedbacks || [],
      page: feedbackPage,
      rowsPerPage: feedbackRowsPerPage,
      onPageChange: handleFeedbackPageChange,
      onRowsPerPageChange: handleFeedbackRowsPerPageChange,
      columns: [
        { key: "sn", label: "SN" },

        { key: "userName", label: "User" },
        { key: "comment", label: "Message" },
        { key: "rating", label: "Rating" },
        { key: "userPhoneNumber", label: "Phone" },
      ],
    },
  ];

  if (isUserLoading || isPropertyLoading || isFeedbackLoading) {
    return <FallbackLoader />;
  }

  return (
    <Box
      sx={{
        padding: isMobile || isTablet ? 2 : 4,
        backgroundColor: theme.palette.background.default,
        minHeight: "100vh",
      }}
    >
      <Grid
        container
        spacing={isMobile || isTablet ? 2 : 3}
        sx={{ marginBottom: isMobile ? 2 : 4 }}
      >
        {countCardItems.map((item) => (
          <Grid
            item
            xs={12}
            sm={4}
            key={item.title}
            sx={{
              minWidth: isMobile || isTablet ? "100%" : "auto",
              maxWidth: "100%",
            }}
          >
            <Card
              elevation={6}
              sx={{
                background: `linear-gradient(145deg, ${alpha(
                  primaryColor,
                  0.8
                )}, ${alpha(primaryColor, 0.6)})`,
                color: "white",
                transition: "transform 0.3s ease-in-out",
                height: "100%",
                "&:hover": {
                  transform: "scale(1.05)",
                },
              }}
            >
              <CardContent
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <Box>
                  <Typography
                    variant={isMobile || isTablet ? "h6" : "h5"}
                    sx={{ opacity: 0.8 }}
                  >
                    {item.title}
                  </Typography>
                  <Typography
                    variant={isMobile ? "h5" : "h4"}
                    fontWeight="bold"
                    sx={{ color: "white" }}
                  >
                    {item.count}
                  </Typography>
                </Box>
                <Typography variant={"h3"}>{item.icon}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box
        sx={{
          backgroundColor: "white",
          borderRadius: 2,
          padding: isMobile ? 2 : 3,
          boxShadow: theme.shadows[3],
          marginBottom: isMobile ? 2 : 4,
          height: isMobile ? "300px" : "400px",
        }}
      >
        <Typography
          variant={isMobile ? "h6" : "h5"}
          sx={{
            color: primaryColor,
            marginBottom: 2,
            fontWeight: "bold",
          }}
        >
          Users Created Per Month
        </Typography>

        {isMobile ? (
          <div
            // ref={chartRef}
            style={{
              width: "100%",
              // height: "400",
              position: "relative",
            }}
          >
            <Line
              data={userChartData}
              options={chartOptions}
              style={{ height: "100%" }}
            />
          </div>
        ) : (
          <Line
            data={userChartData}
            options={chartOptions}
            style={{ height: "100%" }}
          />
        )}
      </Box>

      <Box
        sx={{
          marginTop: isMobile ? 2 : 4,
          backgroundColor: "white",
          borderRadius: 2,
          padding: isMobile ? 2 : 3,
          boxShadow: theme.shadows[3],
        }}
      >
        <Typography
          variant={isMobile ? "h6" : "h5"}
          sx={{
            color: primaryColor,
            marginBottom: 2,
            fontWeight: "bold",
          }}
        >
          Property Locations
        </Typography>

        {isLoaded ? (
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={mapCenter}
            zoom={isMobile ? 12 : 15}
          >
            {validProperties.map((property: any) => (
              <Marker
                key={property.id}
                position={{
                  lat: parseFloat(property.latitude.toString()),
                  lng: parseFloat(property.longitude.toString()),
                }}
                title={property.name}
              />
            ))}
          </GoogleMap>
        ) : (
          <Typography>Loading map...</Typography>
        )}
      </Box>

      {tableSections.map((section) => (
        <Box
          key={section.title}
          sx={{
            marginTop: isMobile ? 2 : 4,
            backgroundColor: "white",
            borderRadius: 2,
            padding: isMobile ? 1 : 3,
            boxShadow: theme.shadows[3],
          }}
        >
          <Typography
            variant={isMobile ? "h6" : "h5"}
            sx={{
              color: primaryColor,
              marginBottom: 2,
              fontWeight: "bold",
            }}
          >
            {section.title}
          </Typography>
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              width: "100%",
              maxWidth: { xs: "95vw", sm: "90vw", md: "95vw", lg: "1200px" },
              mx: "auto",
              overflowX: "auto",
              overflowY: "auto",
              maxHeight: { xs: "70vh", sm: "75vh", md: "80vh", lg: "85vh" },
              "& th": {
                position: "sticky",
                top: 0,
                zIndex: 2,
                backgroundColor: "#bb0000",
                color: "white",
              },
            }}
          >
            <Table
              sx={{
                minWidth: 800,
                width: "100%",
              }}
            >
              <TableHead>
                <TableRow>
                  {section.columns.map((column) => (
                    <TableCell
                      key={column.key}
                      sx={{
                        fontWeight: "bold",
                        color: alpha(primaryColor, 0.8),
                        backgroundColor: alpha(primaryColor, 0.05),
                      }}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {section.data.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={section.columns.length}
                      align="center"
                      sx={{
                        color: alpha(primaryColor, 0.7),
                        fontStyle: "italic",
                      }}
                    >
                      Not found
                    </TableCell>
                  </TableRow>
                ) : (
                  section.data
                    .slice(
                      section.page * section.rowsPerPage,
                      section.page * section.rowsPerPage + section.rowsPerPage
                    )
                    .map((row: User | Feedback, index: number) => (
                      <TableRow
                        key={(row as User | Feedback).id}
                        hover
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                          transition: "background-color 0.2s",
                          "&:hover": {
                            backgroundColor: alpha(primaryColor, 0.05),
                          },
                        }}
                      >
                        {section.columns.map((column) => (
                          <TableCell key={column.key}>
                            {column.key === "sn"
                              ? section.page * section.rowsPerPage + index + 1
                              : (row as any)[column.key] ?? "N/A"}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>

            <TablePagination
              component="div"
              count={section.data.length}
              page={section.page}
              onPageChange={section.onPageChange}
              rowsPerPage={section.rowsPerPage}
              onRowsPerPageChange={section.onRowsPerPageChange}
              sx={{
                "& .MuiTablePagination-toolbar": {
                  color: alpha(primaryColor, 0.7),
                },
              }}
            />
          </TableContainer>
        </Box>
      ))}
    </Box>
  );
};

export default DashboardPage;
